/**
 * Reglement d'une commande servie.
 *
 * En mobile money, le convive donne son numero : la demande de paiement part
 * sur son telephone et il la valide avec son code. Il ne recopie plus de
 * reference. Au comptoir, il annonce simplement son intention.
 *
 * Cet ecran ne termine jamais la commande : il declenche ou declare, puis
 * attend le verdict, qui arrive par l'evenement payment:verified.
 *
 * Voir docs/adr/0012-paiement-verifie-avant-cloture.md et
 * docs/adr/0018-encaisser-le-mobile-money-par-paydunya.md.
 */

import { useState } from 'react';
import type { DeclarePaymentResponse, MobileMoneyProvider, PaymentSummaryDTO } from '@scan2eat/shared';
import { estimatePaymentFeeMinor, formatMoney, MOBILE_MONEY_PROVIDERS } from '@scan2eat/shared';

import { ApiError, apiFetch } from '../api/client.js';
import { getDisplayContext } from '../lib/display-context.js';
import { labels } from '../labels.js';
import { Icon } from './Icon.js';
import styles from './PaymentPanel.module.css';
import celtiisCashLogo from '../assets/operators/celtiis-cash.png';
import moovMoneyLogo from '../assets/operators/moov-money.png';
import mtnMomoLogo from '../assets/operators/mtn-momo.svg';

const PROVIDER_LABEL: Record<MobileMoneyProvider, string> = {
  MTN: 'MTN MoMo',
  MOOV: 'Moov Money',
  CELTIIS: 'Celtiis Cash',
};

/** Logos officiels des operateurs, tels que publies par chacun d'eux. */
const PROVIDER_LOGO: Record<MobileMoneyProvider, string> = {
  MTN: mtnMomoLogo,
  MOOV: moovMoneyLogo,
  CELTIIS: celtiisCashLogo,
};

interface Props {
  orderId: string;
  /** Total de la commande, pour annoncer les frais avant validation. */
  totalMinor: number;
  currency: string;
  payment: PaymentSummaryDTO | null;
  onDeclared: (payment: PaymentSummaryDTO) => void;
}

export function PaymentPanel({ orderId, totalMinor, currency, payment, onDeclared }: Props) {
  const [method, setMethod] = useState<'MOBILE_MONEY' | 'COUNTER' | null>(null);
  const [provider, setProvider] = useState<MobileMoneyProvider>('MTN');
  const [phone, setPhone] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [retrying, setRetrying] = useState(false);
  const [providerMessage, setProviderMessage] = useState<string | null>(null);
  const [checkoutUrl, setCheckoutUrl] = useState<string | null>(null);

  // Le restaurant n'encaisse pas toujours : sans compte marchand, ou hors XOF,
  // le mobile money reste une declaration que le personnel verifie. Le taux
  // sert alors a rien, et les frais ne sont pas annonces.
  const restaurant = getDisplayContext()?.restaurant;
  const collects = restaurant?.mobileMoneyEnabled ?? false;
  const feeMinor = collects
    ? estimatePaymentFeeMinor(totalMinor, restaurant?.paymentFeeRatePermille ?? 0)
    : 0;

  // Un paiement en attente de verification : plus rien a saisir, on informe.
  if (payment?.status === 'DECLARED') {
    return (
      <section className={`${styles.panel} ${styles.waiting}`} aria-live="polite">
        <span className="icon-circle"><Icon name="clock" size={24} /></span>
        <div>
          <h2 className={styles.title}>{labels.payment.declaredTitle}</h2>
          <p className={styles.text}>
            {payment.method === 'COUNTER'
              ? labels.payment.declaredCounter
              : (providerMessage ?? labels.payment.declaredMobile)}
          </p>
          {checkoutUrl && (
            <>
              <a
                href={checkoutUrl}
                target="_blank"
                rel="noreferrer"
                className={`btn-primary btn-block ${styles.checkout}`}
              >
                {labels.payment.openCheckout}
              </a>
              <p className={styles.reference}>{labels.payment.checkoutHelp}</p>
            </>
          )}
          {payment.reference && (
            <p className={styles.reference}>
              {labels.payment.reference_} <span className="mono">{payment.reference}</span>
            </p>
          )}
        </div>
      </section>
    );
  }

  // Paiement rejete : on montre le motif et on laisse recommencer.
  if (payment?.status === 'REJECTED' && !retrying) {
    return (
      <section className={`${styles.panel} ${styles.rejected}`} role="alert">
        <div className={styles.rejectedHead}>
          <Icon name="alert" size={22} />
          <h2 className={styles.title}>{labels.payment.rejected}</h2>
        </div>
        <button type="button" onClick={() => setRetrying(true)} className="btn-primary btn-block">
          {labels.payment.retry}
        </button>
      </section>
    );
  }

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!method) return;
    if (method === 'MOBILE_MONEY' && phone.trim().length === 0) {
      setError(labels.payment.phoneRequired);
      return;
    }

    setSending(true);
    setError(null);
    try {
      const body =
        method === 'MOBILE_MONEY' ? { method, provider, phone: phone.trim() } : { method };
      const result = await apiFetch<DeclarePaymentResponse>(`/public/orders/${orderId}/payment`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      // Le texte du prestataire dit au convive ce qu'il doit faire maintenant.
      // Le formuler a sa place mentirait sur un parcours qu'on ne controle pas.
      setProviderMessage(result.providerMessage ?? (collects ? labels.payment.pushSent : null));
      // En bac a sable, la poussee par operateur n'existe pas : le prestataire
      // rend l'adresse de sa page de paiement, ou l'on regle avec un compte
      // fictif. En production ce champ est toujours vide.
      setCheckoutUrl(result.providerCheckoutUrl);
      onDeclared(result.payment);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : labels.errors.generic);
    } finally {
      setSending(false);
    }
  };

  return (
    <form className={styles.panel} onSubmit={(e) => void submit(e)}>
      <h2 className={styles.title}>{labels.payment.title}</h2>
      <p className={styles.text}>{labels.payment.choose}</p>

      <div className={styles.options} role="radiogroup" aria-label={labels.payment.choose}>
        <button
          type="button"
          role="radio"
          aria-checked={method === 'MOBILE_MONEY'}
          onClick={() => setMethod('MOBILE_MONEY')}
          className={styles.option}
        >
          <span className={styles.optionIcon}><Icon name="phone" size={22} /></span>
          <span className={styles.optionText}>
            <strong>{labels.payment.mobileMoney}</strong>
            <span>{labels.payment.mobileMoneyHint}</span>
          </span>
          <span className={styles.radio} aria-hidden="true" />
        </button>
        <button
          type="button"
          role="radio"
          aria-checked={method === 'COUNTER'}
          onClick={() => setMethod('COUNTER')}
          className={styles.option}
        >
          <span className={styles.optionIcon}><Icon name="cash" size={22} /></span>
          <span className={styles.optionText}>
            <strong>{labels.payment.counter}</strong>
            <span>{labels.payment.counterHint}</span>
          </span>
          <span className={styles.radio} aria-hidden="true" />
        </button>
      </div>

      {method === 'MOBILE_MONEY' && (
        <div className={styles.mobile}>
          <span className="field-label" id="provider-label">{labels.payment.provider}</span>
          <div className={styles.providers} role="radiogroup" aria-labelledby="provider-label">
            {MOBILE_MONEY_PROVIDERS.map((p) => (
              <button
                key={p}
                type="button"
                role="radio"
                aria-checked={provider === p}
                onClick={() => setProvider(p)}
                className={`${styles.provider} ${provider === p ? styles.providerActive : ''}`}
              >
                <img src={PROVIDER_LOGO[p]} alt="" width={40} height={40} className={styles.providerLogo} />
                <span>{PROVIDER_LABEL[p]}</span>
              </button>
            ))}
          </div>

          <div className="field">
            <label htmlFor="phone" className="field-label">{labels.payment.phone}</label>
            <input
              id="phone"
              className="input"
              type="tel"
              inputMode="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              maxLength={20}
              autoComplete="tel"
              aria-invalid={Boolean(error) && phone.trim().length === 0}
            />
            <p className="field-hint">
              {/* Deux parcours differents sous le meme champ : le restaurant
                  declenche le paiement, ou le convive paie de son cote et le
                  personnel verifie. Annoncer le premier quand c'est le second
                  promettrait une demande qui n'arrivera jamais. */}
              {collects ? labels.payment.phoneHelp : labels.payment.phoneHelpManual}
            </p>
          </div>

          {feeMinor > 0 && (
            <div className={styles.fees}>
              <div className={styles.feeRow}>
                <span>{labels.payment.feeNotice}</span>
                <span className="tabular">{formatMoney(feeMinor, currency)}</span>
              </div>
              <div className={`${styles.feeRow} ${styles.feeTotal}`}>
                <span>{labels.payment.totalWithFee}</span>
                <span className="tabular">{formatMoney(totalMinor + feeMinor, currency)}</span>
              </div>
              <p className={styles.reference}>{labels.payment.feeEstimate}</p>
            </div>
          )}
        </div>
      )}

      {error && (
        <p role="alert" className="notice notice--danger" style={{ margin: 0 }}>
          <Icon name="alert" size={18} />
          {error}
        </p>
      )}

      {method && (
        <button type="submit" disabled={sending} className="btn-primary btn-block btn-lg">
          {sending ? labels.payment.submitting : labels.payment.submit}
        </button>
      )}
    </form>
  );
}
